import React from 'react';
import { ChatMessage as ChatMessageType } from '../services/tauri-api';
import { extractLastUserMessageSync } from '../utils/extractLastUserMessage';

interface ChatMessageProps {
  message: ChatMessageType;
  onAIMessageClick?: (promptSessionId: string) => void;
  isGenerating?: boolean;
  isSending?: boolean;
  showGeneratingSpinner?: boolean;
  modelName?: string | null;
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  onAIMessageClick,
  isGenerating = false,
  isSending = false,
  showGeneratingSpinner = false,
  modelName = null,
}) => {
  const isHuman = message.role === 'human';
  const isAssistant = message.role === 'assistant';

  // ユーザーメッセージの場合、最後のユーザーヘッダーと終了タグの間の内容を抽出
  // チャットのmodel属性に基づいてパース
  const displayContent = isHuman 
    ? extractLastUserMessageSync(message.content, modelName)
    : message.content;

  // デバッグ: メッセージ内容を確認
  if (isAssistant && (!message.content || message.content.trim() === '')) {
    console.warn('AIメッセージの内容が空です:', message);
  }

  return (
    <div className={`flex ${isHuman ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`flex max-w-[80%] ${isHuman ? 'flex-row-reverse' : 'flex-row'} items-start gap-2`}>
        {/* アバター */}
        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
          isHuman 
            ? 'bg-blue-600 text-white' 
            : 'bg-gray-200 text-gray-700'
        }`}>
          {isHuman ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="9" y1="9" x2="15" y2="9"></line>
              <line x1="9" y1="15" x2="15" y2="15"></line>
            </svg>
          )}
        </div>

        {/* メッセージコンテンツ */}
        <div className={`flex flex-col ${isHuman ? 'items-end' : 'items-start'}`}>
          <div
          className={`px-4 py-2 rounded-lg ${
            isHuman
              ? 'bg-blue-600 text-white'
              : isAssistant && message.prompt_session_id
              ? 'bg-gray-100 text-gray-900 border border-gray-300 hover:bg-gray-200 cursor-pointer transition-colors'
              : 'bg-gray-100 text-gray-900'
          }`}
          onClick={
            isAssistant && message.prompt_session_id && onAIMessageClick && !isSending
              ? () => onAIMessageClick(message.prompt_session_id!)
              : undefined
          }
          aria-disabled={isAssistant && isSending ? true : undefined}
          style={isAssistant && isSending ? { opacity: 1.0, cursor: 'not-allowed' } : undefined}
          >
            {displayContent && displayContent.trim() ? (
              <div className="text-sm">
                {showGeneratingSpinner ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="whitespace-pre-wrap break-words">{displayContent}</span>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 flex-shrink-0"></div>
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap break-words">{displayContent}</p>
                )}
              </div>
            ) : showGeneratingSpinner ? (
              // 生成中でコンテンツが空の場合、ローディングスピナーのみを表示
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">（メッセージ内容がありません）</p>
            )}
          </div>
          
          {/* タイムスタンプ */}
          <span className="text-xs text-gray-500 mt-1 px-1">
            {new Date(message.timestamp).toLocaleTimeString('ja-JP', { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </span>

          {/* AI返答の場合、ツリー表示のヒント */}
          {isAssistant && message.prompt_session_id && (
            <span className="text-xs text-blue-600 mt-1 px-1 italic">
              クリックしてツリーを表示
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;

